import { useTranslation } from '@hbs-us/ui';

import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';

/**
 * The money-committing Run Payroll confirm dialog (T-22-payroll). Wraps the accessible, focus-trapped
 * `_money/ConfirmDialog` and surfaces the TOTAL GROSS + EMPLOYEE COUNT in the body BEFORE commit —
 * "Run payroll for this period?" / "This creates a payroll run you can export. Review the numbers
 * before confirming." — so the user reviews the exact money figure they are about to commit.
 *
 * The commit is pessimistic: there is NO optimistic UI. `confirmPending` reflects the live mutation
 * state; the committed (locked) run is rendered only after the server responds.
 *
 * Token-only styling (the underlying ConfirmDialog is all `@hbs-us/ui` tokens — no hex, BRAND-06). The
 * total is locale-formatted via the shared money formatters (I18N-05).
 */
export function RunPayrollConfirm({
  open,
  totalGrossPay,
  employeeCount,
  onConfirm,
  onCancel,
  confirmPending,
}: {
  open: boolean;
  totalGrossPay: number;
  employeeCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  confirmPending?: boolean;
}) {
  const { t } = useTranslation();
  const { formatCurrency } = useMoneyFormatters();

  // The money-committing body MUST show the total + count before commit. We render them into the
  // ConfirmDialog body string so they sit inside the focus-trapped, aria-described dialog.
  const body = t('payroll:runConfirm.body', {
    total: formatCurrency(totalGrossPay),
    count: employeeCount,
  });

  return (
    <ConfirmDialog
      open={open}
      heading={t('payroll:runConfirm.heading')}
      body={body}
      confirmLabel={t('payroll:runConfirm.confirm')}
      cancelLabel={t('payroll:runConfirm.cancel')}
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmPending={confirmPending}
    />
  );
}
